import { useCallback } from "react"
import { BSMaterialResultDashboard } from "../dto/beem-shot/BSMaterialResult/BSMaterialResultDashboard"
import { displayDecimal, saveAsFile } from "./helper-service"

export function useDownloadBSMaterialPleaide(
  allBSMaterialResult: BSMaterialResultDashboard[] | undefined,
  openErrorSnackbar: (e: Error) => void,
  setIsDownloadingPleiades: (b: boolean) => void,
  project?: { name: string },
  selectedVariant?: { name: string }
): { handleDownloadBSMaterialPleaide: () => void } {
  const handleDownloadFile = useCallback(
    (
      fileName: string,
      columnNames: string[],
      columns: (keyof BSMaterialResultDashboard | "Nom" | "")[],
      subColumnNames: string[]
    ) => {
      if (allBSMaterialResult) {
        setIsDownloadingPleiades(true)
        const csvContent = toPleiadeString(allBSMaterialResult, columns, subColumnNames, columnNames)
        const bom = new Uint8Array([0xff, 0xfe])
        const utf16le = stringToUTF16LE(csvContent)
        const blob = new Blob([bom, utf16le], { type: "text/plain;charset=utf-16le;" })

        saveAsFile(blob, fileName)
        setIsDownloadingPleiades(false)
      } else {
        openErrorSnackbar(new Error("Les données ne sont pas prêtes à être téléchargées"))
      }
    },
    [allBSMaterialResult, openErrorSnackbar, setIsDownloadingPleiades]
  )

  const handleDownloadBSMaterialPleaide = useCallback(() => {
    const columns: (keyof BSMaterialResultDashboard | "Nom" | "")[] = [
      "Nom",
      "sousLotLabel",
      "quantity",
      "",
      "",
      "nomProduit",
      "ficheId",
      "drv",
      "drv",
      "declarationType",
      "",
      "",
      "",
      "",
      "",
      "",
      "codeOccurrence",
    ]

    const columnNames: string[] = [
      "",
      "Nom",
      "Lot",
      "Quantité",
      "Unité fiche",
      "Coefficient",
      "Fiche",
      "Num. fiche",
      "Durée de vie",
      "Durée de vie Fiche",
      "Type de fiche",
      "Impact CO2 dyn.",
      "Impact CO2 total dyn.",
      "Impact CO2",
      "Impact CO2 total",
      "Bibliothèque",
      "Lot personnalisé",
      "Commentaire",
    ]

    const subColumn: string[] = [
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "Années",
      "Années",
      "",
      "kg eq./unité",
      "kg eq. CO2/m²",
      "kg eq./unité",
      "kg eq. CO2/m²",
      "",
      "",
      "",
    ]
    const fileName = `${project?.name}-${selectedVariant?.name}-Pleiades.txt`

    handleDownloadFile(fileName, columnNames, columns, subColumn)
  }, [handleDownloadFile, project?.name, selectedVariant?.name])

  return { handleDownloadBSMaterialPleaide }
}

export function toPleiadeString<T extends BSMaterialResultDashboard>(
  data: T[],
  columns: (keyof T | "Nom" | "")[],
  subColumn: string[] | undefined = undefined,
  columnNames: string[] | undefined = undefined,
  separator = "\t",
  printHeader = true
): string {
  const rowArray: string[] = []

  if (printHeader) {
    if (columnNames && columnNames.length - 1 === columns.length) {
      rowArray.push(columnNames.join(separator))
      if (subColumn) {
        rowArray.push(subColumn.join(separator))
      }
    } else {
      rowArray.push(columns.join(separator))
    }
  }

  data.forEach((item, index) => {
    const row: string = [
      (index + 1).toString(),
      ...columns.map((col) => {
        if (!col) {
          return ""
        }

        if (col === "Nom") {
          const value = `${item.bsItemLabel ?? ""} - ${item.bsMacroComponentLabel ?? ""}`.trim()
          return valueToCsvString(value)
        }

        const value: any = item[col]
        return valueToCsvString(value)
      }),
    ].join(separator)

    rowArray.push(row)
  })

  return rowArray.join("\n")
}

function valueToCsvString(value: any): string {
  if (typeof value === "string") {
    return `${value.replace(/"/g, '""')}`
  } else if (typeof value === "number") {
    return displayDecimal(value, 1)
  } else if (typeof value === "boolean") {
    return value ? "TRUE" : "FALSE"
  } else if (value === undefined || value === null) {
    return ""
  } else {
    return `${value}`
  }
}

function stringToUTF16LE(str: string): Uint8Array {
  const buf = new Uint8Array(str.length * 2)
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < str.length; i++) {
    // eslint-disable-next-line no-bitwise
    const code = str.charCodeAt(i)
    // eslint-disable-next-line no-bitwise
    buf[i * 2] = code & 0xff
    // eslint-disable-next-line no-bitwise
    buf[i * 2 + 1] = code >> 8
  }
  return buf
}
